// src/components/Search.js
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useSearchParams, useNavigate, useParams, useLocation } from 'react-router-dom';
import { searchSanctions, getVerification, saveSearchResults, getSearchResultsById } from '../services/searchService';
import { vectorSearchSanctions } from '../services/vectorSearchService';
import { useAuth } from '../contexts/AuthContext';
import { validateSearchInput } from '../utils/validation';
import SearchForm from './SearchForm';
import SearchResults from './SearchResults';
import WelcomeSection from './WelcomeSection';
import SearchLimitWarning from './SearchLimitWarning';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { logSearchEvent, LOG_LEVELS } from '../services/loggingService';
import { useTranslation } from '../hooks/useTranslation';
import { getUsageStats } from '../services/usageService';
import { useToast } from '../contexts/ToastContext';

const Search = forwardRef((props, ref) => {
  // Get URL parameters
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: verificationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get translation function
  const t = useTranslation();
  
  // Get current user and organization from the auth context
  const { currentUser, currentOrg } = useAuth();
  
  // Get toast functions
  const { showError, showWarning } = useToast();
  
  // Log authentication state on component mount
  useEffect(() => {
    console.log("Authentication state:", { 
      isAuthenticated: !!currentUser,
      userId: currentUser?.uid || 'not logged in'
    });
  }, [currentUser]);
  
  // Track whether we have a previous page to return to
  const [hasValidPreviousPage, setHasValidPreviousPage] = useState(false);
  const [previousPageType, setPreviousPageType] = useState('search'); // Default to 'search'
  
  // Check if we have a previous page within our app when the component mounts
  useEffect(() => {
    // 1. First check location.state (when navigated programmatically)
    if (location.state?.from) {
      setHasValidPreviousPage(true);
      setPreviousPageType(location.state.from);
      return;
    }
    
    // 2. Check if we have navigation history
    if (window.history.length > 1) {
      setHasValidPreviousPage(true);
      
      // 3. Try to determine the previous page type from the referrer
      // We can try to check document.referrer, but this will only work if coming from the same origin
      const referrer = document.referrer;
      if (referrer && referrer.includes(window.location.origin)) {
        const path = new URL(referrer).pathname;
        
        // Map paths to page types
        if (path === '/' || path.includes('search')) {
          setPreviousPageType('search');
        } else if (path.includes('cases')) {
          setPreviousPageType('cases');
        } else if (path.includes('massUpload')) {
          setPreviousPageType('report');
        } else if (path.includes('history')) {
          setPreviousPageType('history');
        } else if (path.includes('monitoring')) {
          setPreviousPageType('monitoring');
        }
      }
    }
  }, [location]);

  // Get the appropriate back button text based on where the user came from
  const getBackButtonText = () => {
    // Map page types to their button text
    const backTextMap = {
      search: t('common.backToSearch'),
      cases: t('common.backToCases'),
      report: t('common.backToReport'),
      history: t('common.backToHistory'),
      monitoring: t('common.backToMonitoring')
    };
    
    // Return the mapped text or a default if not found
    return backTextMap[previousPageType] || t('common.back');
  };
  
  // Handle going back in history
  const handleGoBack = () => {
    if (hasValidPreviousPage) {
      navigate(-1); // Go back one step in history
    }
  };
  
  // Determine display mode based on props or URL
  // For backward compatibility, support both verificationMode prop and displayMode prop
  const getDisplayMode = () => {
    if (props.displayMode) return props.displayMode;
    if (props.verificationMode || verificationId) return 'verification';
    return 'search';
  }

  const displayMode = getDisplayMode();
  const isVerificationMode = displayMode === 'verification';
  
  // Search state
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [lastSearchedTerm, setLastSearchedTerm] = useState(searchParams.get('q') || '');
  const [entityType, setEntityType] = useState(searchParams.get('type') || 'person'); // Get from URL params or default to person
  const [loading, setLoading] = useState(false);
  const [isHistoricalSearch, setIsHistoricalSearch] = useState(false);
  const [searchProgress, setSearchProgress] = useState({ processed: 0, total: 0 });
  const [searchResults, setSearchResults] = useState([]);
  const [searchStats, setSearchStats] = useState(null);
  const [hasSearched, setHasSearched] = useState(!!searchParams.get('q'));
  const [verificationData, setVerificationData] = useState(null);
  const [searchId, setSearchId] = useState(searchParams.get('id') || null);
  
  // Use the error handler hook instead of local error state
  const { error, handleError, clearError } = useErrorHandler();
  
  // Usage stats for limit checking
  const [usageStats, setUsageStats] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  
  // Sticky search state
  const searchFormRef = useRef(null);
  const [stickyVisibility, setStickyVisibility] = useState(0);
  const [isFullySticky, setIsFullySticky] = useState(false);
  
  // Perform search on initial load if query parameter or id exists
  useEffect(() => {
    const query = searchParams.get('q');
    const id = searchParams.get('id');
    const type = searchParams.get('type');
    
    if (type) {
      setEntityType(type);
    }
    
    if (id) {
      setSearchId(id);
      loadSearchById(id);
    } else if (query) {
      setSearchTerm(query);
      handleSearch(null, query);
    }
  }, []);
  
  // Load verification data if in verification mode
  useEffect(() => {
    if (isVerificationMode && verificationId && currentUser && currentOrg) {
      // Check if there's a searchId in the URL query parameters
      const urlSearchId = searchParams.get('searchId');
      if (urlSearchId) {
        setSearchId(urlSearchId);
      }
      
      loadVerificationData(verificationId);
    }
  }, [isVerificationMode, verificationId, currentUser, currentOrg]);

  // Load usage stats when organization changes
  useEffect(() => {
    const loadUsageStats = async () => {
      if (!currentOrg?.id) {
        setUsageStats(null);
        return;
      }
      
      try {
        setUsageLoading(true);
        const stats = await getUsageStats(currentOrg.id);
        setUsageStats(stats);
      } catch (error) {
        console.error('Error loading usage stats:', error);
        setUsageStats(null);
      } finally {
        setUsageLoading(false);
      }
    };

    loadUsageStats();
  }, [currentOrg?.id]);
  
  // Load data for a specific verification ID
  const loadVerificationData = async (id) => {
    setLoading(true);
    setIsHistoricalSearch(true);
    clearError();
    
    console.log('Loading verification data for ID:', id, 'Org:', currentOrg?.id);
    
    try {
      const data = await getVerification(id, currentOrg?.id);
      if (data) {
        console.log('Verification data loaded:', {
          searchTerm: data.searchTerm,
          resultsCount: data.results?.length,
          searchId: data.searchId,
          displayMode
        });
        
        setVerificationData(data);
        setSearchTerm(data.searchTerm || '');
        setLastSearchedTerm(data.searchTerm || '');
        setEntityType(data.entityType || 'person'); // Set entity type from data or default to person
        setSearchResults(data.results || []);
        setSearchStats(data.stats || null);
        setHasSearched(true);
        
        // Set the searchId to enable review functionality in verification mode
        // This ensures dismiss/confirm buttons and case status appear
        const effectiveSearchId = data.searchId || id;
        console.log('Setting searchId to:', effectiveSearchId);
        setSearchId(effectiveSearchId);
      } else {
        handleError(new Error('Verification record not found'), { 
          component: 'Search', 
          action: 'loadVerificationData', 
          verificationId: id
        });
      }
    } catch (err) {
      handleError(err, { 
        component: 'Search', 
        action: 'loadVerificationData', 
        verificationId: id
      });
    } finally {
      setLoading(false);
      setIsHistoricalSearch(false);
    }
  };
  
  // Load search results by ID
  const loadSearchById = async (id) => {
    if (!id) return;
    
    setLoading(true);
    setIsHistoricalSearch(true);
    clearError();
    
    try {
      const data = await getSearchResultsById(id, currentOrg?.id);
      if (data) {
        setSearchTerm(data.searchTerm || '');
        setLastSearchedTerm(data.searchTerm || '');
        setEntityType(data.entityType || 'person'); // Set entity type from data or default to person
        setSearchResults(data.results || []);
        setSearchStats(data.stats || null);
        setHasSearched(true);
      } else {
        handleError(new Error('Search results not found'), { 
          component: 'Search', 
          action: 'loadSearchById', 
          searchId: id
        });
        // If not found, clear the id from URL
        setSearchParams({ q: searchParams.get('q') || '' });
      }
    } catch (err) {
      handleError(err, { 
        component: 'Search', 
        action: 'loadSearchById', 
        searchId: id
      });
      // If error, clear the id from URL
      setSearchParams({ q: searchParams.get('q') || '' });
    } finally {
      setLoading(false);
      setIsHistoricalSearch(false);
    }
  };
  
  // Expose resetSearch method to parent components
  useImperativeHandle(ref, () => ({
    resetSearch: () => {
      setSearchTerm('');
      setLastSearchedTerm('');
      setEntityType('person'); // Reset to default entity type
      setSearchResults([]);
      setSearchStats(null);
      setHasSearched(false);
      clearError();
      setSearchProgress({ processed: 0, total: 0 });
      setVerificationData(null);
      setSearchId(null);
      setSearchParams({});
      
      // If in verification mode, navigate back to search
      if (isVerificationMode) {
        navigate('/');
      }
    },
    getSearchTerm: () => searchTerm,
    setSearchTerm: (term) => setSearchTerm(term),
    handleSearch: () => handleSearch()
  }));
  
  // Refresh usage stats after successful search
  const refreshUsageStats = async () => {
    if (!currentOrg?.id) return;
    
    try {
      const stats = await getUsageStats(currentOrg.id);
      setUsageStats(stats);
    } catch (error) {
      console.error('Error refreshing usage stats:', error);
    }
  };

  // Handle search submission
  const handleSearch = async (e, forcedSearchTerm = null) => {
    e?.preventDefault();
    
    if (loading) return;
    
    const sanitizedInput = validateSearchInput(forcedSearchTerm || searchTerm);
    
    if (!sanitizedInput) {
      handleError(new Error(t('search.invalidInput')), { 
        component: 'Search', 
        action: 'handleSearch', 
        searchTerm: sanitizedInput
      });
      return;
    }

    // Check if user has reached their search limit before starting (cached check)
    if (usageStats && !usageStats.allowed) {
      const errorMessage = `Monthly search limit exceeded. You have used ${usageStats.used} of ${usageStats.totalLimit} searches this month.`;
      handleError(new Error(errorMessage), { 
        component: 'Search', 
        action: 'handleSearch', 
        searchTerm: sanitizedInput,
        limitInfo: usageStats
      });
      return;
    }
    
    setLoading(true);
    clearError();
    
    // Do a real-time limit check before starting the search to handle multi-user scenarios
    try {
      const realTimeStats = await getUsageStats(currentOrg?.id);
      if (!realTimeStats.allowed) {
        // Update our cached stats and show the limit error
        setUsageStats(realTimeStats);
        
        // Show toast notification
        showError(
          `Search limit exceeded! You have used ${realTimeStats.used} of ${realTimeStats.totalLimit} searches this month.`,
          5000 // Show for 5 seconds
        );
        
        // Don't use the generic error handler - just stop here
        setLoading(false);
        return;
      }
      // Update our cached stats with fresh data
      setUsageStats(realTimeStats);
    } catch (limitCheckError) {
      console.error('Error checking real-time limits:', limitCheckError);
      // Continue with search if limit check fails - the backend will catch it
    }
    setLastSearchedTerm(sanitizedInput);
    setHasSearched(true);
    setSearchProgress({ processed: 0, total: 100 });
    
    try {
      // Log the search event
      await logSearchEvent('search_started', { 
        searchTerm: sanitizedInput,
        entityType: entityType,
        userId: currentUser?.uid || 'anonymous'
      });
      
      // Use the vector search function instead of the optimized search
      const { results, stats } = await vectorSearchSanctions(
        sanitizedInput, 
        (processInfo) => {
          setSearchProgress(processInfo);
        },
        currentUser?.uid || null,
        entityType,
        currentOrg?.id || null
      );
      
      // Update the results state
      setSearchResults(results);
      setSearchStats(stats);
      
      // Refresh usage stats after successful search
      await refreshUsageStats();
      
      // Save search to history and get a searchId if user is logged in
      if (currentUser && currentOrg) {
        try {
          const savedSearchId = await saveSearchResults({
            searchTerm: sanitizedInput,
            entityType: entityType,
            results: results || [],
            stats: stats,
            userId: currentUser.uid,
            orgId: currentOrg.id
          });
          
          // If we got a searchId back, update the URL
          if (savedSearchId) {
            setSearchId(savedSearchId);
            setSearchParams({ 
              q: sanitizedInput,
              id: savedSearchId,
              type: entityType
            });
            
            // Log search completed with ID
            await logSearchEvent('search_completed', { 
              searchTerm: sanitizedInput,
              entityType: entityType,
              searchId: savedSearchId,
              resultsCount: results ? results.length : 0,
              userId: currentUser.uid
            });
          }
        } catch (saveErr) {
          console.error('Error saving search results:', saveErr);
          // Don't show this error to user, just log it
          await logSearchEvent('search_save_failed', { 
            searchTerm: sanitizedInput,
            entityType: entityType,
            error: saveErr.message,
            userId: currentUser?.uid || 'anonymous'
          }, LOG_LEVELS.ERROR);
        }
      } else {
        // Log search completed without saving (anonymous or no organization)
        await logSearchEvent('search_completed', { 
          searchTerm: sanitizedInput,
          entityType: entityType,
          resultsCount: results ? results.length : 0,
          userId: 'anonymous'
        });
      }
      
    } catch (err) {
      console.error('Search error:', err);
      
      // Handle search limit errors specially
      if (err.code === 'SEARCH_LIMIT_EXCEEDED') {
        // Refresh usage stats when we get a limit exceeded error
        try {
          await refreshUsageStats();
        } catch (refreshError) {
          console.error('Error refreshing usage stats after limit exceeded:', refreshError);
        }
        
        const limitInfo = err.limitInfo;
        // Show toast notification instead of generic error
        showError(
          `Search limit exceeded! You have used ${limitInfo.used} of ${limitInfo.totalLimit} searches this month.`,
          5000 // Show for 5 seconds
        );
        
        // Clear any existing generic errors
        clearError();
      } else {
        handleError(err, { 
          component: 'Search', 
          action: 'handleSearch', 
          searchTerm: sanitizedInput
        });
      }
      
      // Log search error
      await logSearchEvent('search_error', { 
        searchTerm: sanitizedInput,
        error: err.message,
        userId: currentUser?.uid || 'anonymous'
      }, LOG_LEVELS.ERROR);
    } finally {
      setLoading(false);
    }
  };
  
  // Effect to show/hide the sticky search bar
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          // Get the search form reference
          const searchForm = searchFormRef.current;
          if (!searchForm) {
            ticking = false;
            return;
          }
          
          // Get the search form's position and dimensions
          const rect = searchForm.getBoundingClientRect();
          
          // Calculate how much of the form is out of view
          const outOfView = Math.min(0, rect.bottom);
          const totalHeight = rect.height;
          
          // Set visibility percentage (0 = fully visible, 1 = fully hidden)
          const visibilityPercentage = Math.min(1, Math.max(0, -outOfView / totalHeight));
          setStickyVisibility(visibilityPercentage);
          
          // Check if we're fully sticky (search bar completely out of view)
          // Add some buffer to prevent rapid toggling
          const fullySticky = rect.bottom < -5;
          setIsFullySticky(fullySticky);
          
          // Add or remove class to body for wider layout adjustments
          // Use the same buffer to prevent flickering
          if (fullySticky) {
            document.body.classList.add('sticky-search-active');
          } else if (rect.bottom > 5) {
            // Only remove when search form is clearly visible
            document.body.classList.remove('sticky-search-active');
          }
          
          ticking = false;
        });
        ticking = true;
      }
    };
    
    // Skip for verification mode, we don't need the scroll handler
    if (isVerificationMode) return;
    
    // Otherwise, set up the scroll event handler with passive flag for better performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.classList.remove('sticky-search-active');
    };
  }, [isVerificationMode]);
  
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      {/* Search Limit Warning - Show for authenticated users only */}
      {currentUser && currentOrg && !isVerificationMode && (
        <SearchLimitWarning 
          usageStats={usageStats} 
          loading={usageLoading}
        />
      )}

      {/* Verification UI */}
      {isVerificationMode && verificationData && (
        <div className="bg-blue-50 dark:bg-gray-800 border border-blue-200 dark:border-gray-700 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div className="verification-info">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{verificationData.searchTerm}</h2>
            <div className="flex items-center space-x-4 mt-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('results.verificationId')}: <span className="font-medium text-gray-800 dark:text-gray-200">{verificationId}</span>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('search.entityType') || 'Entity Type'}: 
                <span className="font-medium text-gray-800 dark:text-gray-200 ml-1">
                  {verificationData.entityType === 'enterprise' 
                    ? (t('search.enterpriseType') || 'Enterprises') 
                    : (t('search.personType') || 'People')}
                </span>
              </p>
            </div>
          </div>
          <button 
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 text-white hover:from-blue-700 hover:to-indigo-700 dark:hover:from-blue-800 dark:hover:to-indigo-800 text-sm font-medium rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-blue-600"
            onClick={handleGoBack}
          >
            {getBackButtonText()}
          </button>
        </div>
      )}
      
      {/* Show boxed search form during initial view and while loading */}
      {(!isVerificationMode || !verificationData) && (loading || !hasSearched) && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-8 px-10 mb-8">
          <SearchForm
            ref={searchFormRef}
            handleSearch={handleSearch}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            entityType={entityType}
            setEntityType={setEntityType}
            isLoading={loading}
            usageStats={usageStats}
          />
          
          {!loading && !hasSearched && (
            <WelcomeSection 
              setSearchTerm={setSearchTerm} 
              handleSearch={handleSearch}
            />
          )}
          
          {/* Show progress bar inside the container when loading */}
          {loading && (
            <SearchResults
              results={searchResults}
              stats={searchStats}
              searchTerm={lastSearchedTerm}
              isLoading={loading}
              isSearchComplete={!loading && hasSearched}
              searchProgress={searchProgress}
              verificationId={verificationData?.verificationId || searchStats?.verificationId}
              isVerificationMode={isVerificationMode}
              searchId={searchId || (isVerificationMode ? verificationId : null)}
              displayMode={displayMode}
              isHistoricalSearch={isHistoricalSearch}
              entityType={entityType}
            />
          )}
        </div>
      )}

      {/* Search form and results when showing results */}
      {(!isVerificationMode || !verificationData) && hasSearched && !loading && (
        <>
          <SearchForm
            ref={searchFormRef}
            handleSearch={handleSearch}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            entityType={entityType}
            setEntityType={setEntityType}
            isLoading={loading}
            usageStats={usageStats}
          />
          
          <SearchResults
            results={searchResults}
            stats={searchStats}
            searchTerm={lastSearchedTerm}
            isLoading={loading}
            isSearchComplete={!loading && hasSearched}
            searchProgress={searchProgress}
            verificationId={verificationData?.verificationId || searchStats?.verificationId}
            isVerificationMode={isVerificationMode}
            searchId={searchId || (isVerificationMode ? verificationId : null)}
            displayMode={displayMode}
            isHistoricalSearch={isHistoricalSearch}
            entityType={entityType}
          />
        </>
      )}
      
      {/* Verification mode results */}
      {isVerificationMode && verificationData && (
        <SearchResults
          results={searchResults}
          stats={searchStats}
          searchTerm={lastSearchedTerm}
          isLoading={loading}
          isSearchComplete={!loading && hasSearched}
          searchProgress={searchProgress}
          verificationId={verificationData?.verificationId || searchStats?.verificationId}
          isVerificationMode={isVerificationMode}
          searchId={searchId || verificationId}
          displayMode={displayMode}
          isHistoricalSearch={isHistoricalSearch}
          entityType={entityType}
        />
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-md relative flex items-start space-x-2 mt-4" role="alert">
          <i className="fa-solid fa-exclamation-circle mt-1"></i>
          <p className="flex-1">{error}</p>
        </div>
      )}
      
      {/* Show search ID if available and we're in search mode */}
      {!isVerificationMode && searchId && !loading && searchResults && searchResults.length > 0 && (
        <div className="mt-6 flex items-center justify-center text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium mr-2">{t('search.searchId')}:</span>
          <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded mr-2 text-gray-800 dark:text-gray-200">{searchId}</span>
          <span className="ml-2 mr-2">|</span>
          <span className="font-medium mr-2">{t('search.entityType') || 'Entity Type'}:</span>
          <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded mr-2 text-gray-800 dark:text-gray-200">
            {entityType === 'enterprise' 
              ? (t('search.enterpriseType') || 'Enterprises') 
              : (t('search.personType') || 'People')}
          </span>
          <button 
            className="p-1 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800"
            onClick={() => {
              const shareUrl = `${window.location.origin}/?id=${searchId}&type=${entityType}`;
              navigator.clipboard.writeText(shareUrl)
                .then(() => alert(t('search.linkCopied')))
                .catch(err => {
                  console.error('Error copying link:', err);
                  alert(t('errors.copyFailed'));
                });
            }}
            title={t('search.copyLink')}
          >
            <i className="fa-solid fa-link"></i>
          </button>
        </div>
      )}
    </div>
  );
});

export default Search;